import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Modal from '~/components/Modal';
import PrintableComponent from '~/components/PrintableComponent';
import { productPaths } from '~/routes/paths';
import api, { endpoints } from '~/services/api';
import ProductModalityService from '~/services/product-modality-service';
import { showMessageError } from '~/util/errorutils';
import { inputNumber } from '~/util/stringutils';
import permissions from '~/util/permissions';
import { accessDenied } from '~/util/accessDenied';
import { SidebarPageHeader } from '~/components/SidebarPageHeader';
import TableComponent from '~/components/TableComponent';
import SearchComponent from './SearchComponent';
import { useFitler } from '~/hook/useFilter';
import {
  CancelButton,
  Container,
  DeleteButton,
  ModalContainer
} from './styles';

function ProductModality({ history }) {
  const { filters, setFilters } = useFitler();
  const [loading, setLoading] = useState(false);
  const [inputCodigo, setInputCodigo] = useState('');
  const [inputName, setInputName] = useState('');
  const [searchResponse, setSearchResponse] = useState([]);
  const [deleteItem, setDeleteItem] = useState(null);
  const [searchResponseReport, setSearchResponseReport] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [countFilter, setCountFilter] = useState(0);
  const [pageParameters, setPageParams] = useState({
    page: 0, rowsPerPage: 50,
  });
  const [formData, setFormData] = useState({
    id: null, text: '',
  });

  let PrintableElement = React.useRef();

  const { permissions: userPermissions } = useSelector(
    state => state.user.data
  );
  const userCanCreate = useMemo(
    () => userPermissions.includes(permissions.create_modalidade),
    [userPermissions]
  );
  const userCanEdit = useMemo(
    () => userPermissions.includes(permissions.edit_modalidade),
    [userPermissions]
  );
  const userCanDelete = useMemo(
    () => userPermissions.includes(permissions.delete_modalidade),
    [userPermissions]
  );

  useEffect(() => {
    findAllByFilter(pageParameters, handleFilters());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleFilters() {
    let count = 0;

    if (filters.id) {
      setInputCodigo(filters.id);
      count += 1;
    }
    if (filters.text) {
      setInputName(filters.text);
      count += 1;
    }

    setCountFilter(count);

    return filters;
  }

  function handleSetSearchResponse(result, total) {
    if (result && result.length) {
      setSearchResponse(result);
      setTotalItems(total);
    } else {
      setSearchResponse([]);
      setTotalItems(0);
    }
  }

  function handleInputCodigoChange(event) {
    const {
      target: { value },
    } = event;

    setInputCodigo(inputNumber(value));
  }

  function handleInputNameChange(event) {
    const {
      target: { value },
    } = event;

    setInputName(value);
  }

  async function validateForm(success) {
    let count = 0;
    const data = {};
    if (inputCodigo) {
      data.id = inputCodigo.trim();
      count += 1;
    }
    if (inputName.length) {
      data.text = inputName.trim();
      count += 1;
    }
    setCountFilter(count);
    setFormData(data);
    setFilters(data);
    await success(data);
  }

  function handleSetSearchResponseReport(result) {
    if (result && result.length) {
      setSearchResponseReport(result);
    } else {
      setSearchResponseReport([]);
    }
  }

  async function findAllByFilterReport(pageParams, formDataParam) {
    setLoading(true);
    try {
      const data = await ProductModalityService.getProductModalities({
        ...formDataParam,
      });
      if (data.resultado.length) {
        handleSetSearchResponseReport(data.resultado);
      } else {
        handleSetSearchResponseReport();
        toast('Não encontramos modalidades de produtos para esta pesquisa!', {
          type: toast.TYPE.SUCCESS,
        });
      }
    } catch (error) {
      showMessageError(error);
    } finally {
      setLoading(false);
    }
  }

  async function findAllByFilter(pageParams, dataParams) {
    setPageParams(pageParams);
    setLoading(true);
    try {
      const { page, rowsPerPage } = pageParams;
      const data = await ProductModalityService.getProductModalities({
        ...(dataParams || formData),
        limit: page * rowsPerPage + rowsPerPage,
        offset: page * rowsPerPage,
      });
      if (data.resultado.length) {
        handleSetSearchResponse(data.resultado, data.total);
      } else {
        handleSetSearchResponse();
        toast('Não encontramos modalidades de produtos para esta pesquisa!', {
          type: toast.TYPE.SUCCESS,
        });
      }
    } catch (error) {
      showMessageError(error);
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(event) {
    event.preventDefault();
    validateForm(async data => {
      await findAllByFilter(pageParameters, data);
    });
  }

  async function handleDelete(event) {
    event.preventDefault();
    try {
      setLoading(true);
      const { data: response } = await api.delete(
        endpoints.product.modality.deleteProductModality(deleteItem.id)
      );
      if (!response.data.ok) {
        toast('Erro ao deletar a modalidade de produto!', {
          type: toast.TYPE.ERROR,
        });
      } else {
        const newSearchResponse = searchResponse.filter(
          resp => resp.id !== deleteItem.id
        );
        handleSetSearchResponse(newSearchResponse, totalItems - 1);
        toast(response.message, { type: toast.TYPE.SUCCESS });
      }
    } catch (error) {
      showMessageError(error, 'Erro ao deletar a modalidade de produto!');
    } finally {
      setLoading(false);
      setDeleteItem(null);
    }
  }

  return (
    <>
      <PrintableComponent
        ref={ref => (PrintableElement = ref)}
        title="Modalidade"
        handlePageUpdate={findAllByFilterReport}
        headerLabels={[
          { text: 'Código', width: 'auto' },
          { text: 'Nome', width: '70%' },
        ]}
        dataObjects={searchResponseReport}
        fieldsArray={['id', 'nome']}
        filters={[
          { name: 'Código', value: inputCodigo },
          { name: 'Modalidade', value: inputName },
        ]}
      />
      <Container>
        <Modal loading={loading} show={loading || !!deleteItem}>
          <ModalContainer>
            <h2>Confirmar exclusão</h2>
            <span>
              Tem certeza que deseja deletar modalidade de produto:
              {` ${!!deleteItem &&
                ((!!deleteItem.nome && deleteItem.nome) || deleteItem.id)}?`}
            </span>
            <div>
              <CancelButton onClick={() => setDeleteItem(null)}>
                Fechar
              </CancelButton>
              <DeleteButton onClick={handleDelete}>Deletar</DeleteButton>
            </div>
          </ModalContainer>
        </Modal>

        <SidebarPageHeader
          mainMenu="Produto"
          pageName="Modalidade"
          button1={{
            label: 'Nova Modalidade',
            link: productPaths.addProductModality,
            permission: userCanCreate,
            main: true,
          }}
          showReport
          printableRef={() => PrintableElement}
          onBeforePrintCallback={async () => {
            await validateForm(async data => {
              await findAllByFilterReport(pageParameters, data);
            });
          }}
          openButtonSelect
        />

        <SearchComponent
          countFilter={countFilter}
          codeValue={inputCodigo}
          nameValue={inputName}
          handleCodeChange={handleInputCodigoChange}
          handleNameChange={handleInputNameChange}
          handleSubmit={handleSubmit}
        />

        <TableComponent
          headerLabels={[
            { text: 'Código', width: 'auto' },
            { text: 'Nome', width: '70%' },
          ]}
          dataObjects={searchResponse}
          fieldsArray={['id', 'nome']}
          editPermission={userCanEdit}
          handleEdit={row =>
            history.push(productPaths.editProductModality, {
              id: row.id,
            })
          }
          deletePermission={userCanDelete}
          handleDelete={userCanDelete
            ? row => setDeleteItem(row)
            : () => accessDenied(history)
          }
          totalItems={totalItems}
          handlePageUpdate={findAllByFilter}
        />
      </Container>
    </>
  );
}

ProductModality.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
};

export default ProductModality;
